import React, { useContext, useReducer, useRef, useEffect, useCallback, useState } from 'react';
import {
	IFilterValues,
	TTypeOfModalWindow,
	TUsersCountOfItems,
	TUsersSortBy,
	UsersContext,
	usersInitialState,
} from './usersContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { usersReducer } from './usersReducer';
import { UsersActionTypes } from './usersActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedUsers, deleteUser } from 'backend/users';
import { IUsers, IGetPaginatedUsers } from 'types';
import { AxiosResponse } from 'axios';

const UsersState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(usersReducer, usersInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IUsers>, IGetPaginatedUsers>(
		isMounted.current ? getPaginatedUsers : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			roleId: state.IDSelectedRole,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByRole: state.sortBy === 'RoleA-Z' || state.sortBy === 'RoleZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: UsersActionTypes.SET_USERS,
				payload: {
					users: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteUser(state.IDForDeletion);
			const filteredUsers = state.users.users.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: UsersActionTypes.SET_USERS,
				payload: {
					users: {
						usersCount: state.users.usersCount - 1,
						users: filteredUsers,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: UsersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.users]);

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: UsersActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setIDRole = (value: Nullable<string>) => {
		dispatch({
			type: UsersActionTypes.SET_ID_ROLE,
			payload: {
				IDSelectedRole: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: UsersActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TUsersSortBy) => {
		dispatch({
			type: UsersActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TUsersCountOfItems) => {
		dispatch({
			type: UsersActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: UsersActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: UsersActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: UsersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: UsersActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<UsersContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDRole,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				users: state.users,
				searchString: state.searchString,
				IDSelectedRole: state.IDSelectedRole,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</UsersContext.Provider>
	);
};

export default UsersState;
